import React, { useEffect, useState } from 'react';
import useSortByLoc from '../hooks/useSortByLoc';
import {
  Filters,
  FiltersMobile,
  SearchFilter,
  PerPage,
  SortBy,
  FeaturedCars,
  LoadingSpinner,
} from '.';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWpCars, fetchMcCars } from '../store/thunks/vehicles.thunks';
import {
  changeLocation,
  reset,
  setDataBySearch,
  setSortBy,
} from '../store/slices/motorcycles.slice';
import { VehicleGrid, VehicleList } from '.';
import { noResultsMessage, endOfListMessage } from '../contants/data';
import Cookies from 'js-cookie';

// ICONS
import { CiGrid41, CiGrid2H } from 'react-icons/ci';

const Motorcycles = () => {
  const category = 28922;
  const otherApiCategory = 'motorcycle';
  const [gridLayout, setGridLayout] = useState(true);
  const [init, setInit] = useState(false);
  let {
    data: cars,
    originalData,
    sorted_data,
    originalSorted_data,
    isLoading,
    searchTerm,
    sortBy,
  } = useSelector((state) => state.motorcycles);
  const VehicleComponent = gridLayout ? VehicleGrid : VehicleList;
  const filterOptions = [
    'Make',
    'Model',
    'Condition',
    'Price',
    'Year',
    'Mileage',
    'Horsepower',
    'Engine Size',
    'Length',
    'Exterior Color',
    'Seller Type',
    'Title Status',
  ];

  // Cookie
  const cookieExpiryDays = 7; // Cookie will expire in 7 days
  const dispatch = useDispatch();

  // API PAGES
  const mcTotalPages = 5;
  let wpTotalPages = 1;
  let pageKeyWp = 1;
  let pageKeyMc = 1;

  // GEOLOCATE
  const {
    lat,
    lng,
    radius: initialRadus,
  } = useSelector((state) => state.motorcycles.filters.location);

  // LOAD MORE
  const [perPage, setPerPage] = useState(12);
  let carsData = useSortByLoc(cars, lat, lng);

  if (sorted_data.length > 0) {
    carsData = sorted_data.slice(0, perPage);
  } else {
    if (sortBy === 'Closest to Me') {
      carsData = carsData.slice(0, perPage);
    } else {
      carsData = [];
    }
  }

  // PRICE
  const { min: priceMin, max: priceMax } = useSelector(
    (state) => state.motorcycles.filters.price_range
  );

  // YEAR
  const { min: yearMin, max: yearMax } = useSelector(
    (state) => state.motorcycles.filters.year_range
  );

  // MILES
  const { min: milesMin, max: milesMax } = useSelector(
    (state) => state.motorcycles.filters.mile_range
  );

  // CONDITION
  const { condition: initCondition } = useSelector(
    (state) => state.motorcycles.filters
  );

  // HORSEPOWER
  const { min: horsepowerMin, max: horsepowerMax } = useSelector(
    (state) => state.motorcycles.filters.horsepower_range
  );

  // EXTERIOR COLOR
  const { extColors: initExtColors } = useSelector(
    (state) => state.motorcycles.filters
  );

  // SELLER
  const { seller: initSeller } = useSelector(
    (state) => state.motorcycles.filters
  );

  // TITLE
  const { cleanTitle: iniCleanTitle } = useSelector(
    (state) => state.motorcycles.filters
  );

  const loadMore = () => {
    setPerPage(perPage + perPage);
  };

  const perPageUpdate = (pageNum) => {
    setPerPage((prevState) => (prevState = pageNum.value));
  };

  const handleSortBy = (sortedBy) => {
    dispatch(setSortBy(sortedBy.value));
  };

  const filter = (params) => {
    const {
      finalLat,
      finalLng,
      radius,
      make,
      model,
      priceVal,
      yearVal,
      milesVal,
      condition,
      horsepowerVal,
      extColors,
      seller,
      cleanTitle,
    } = params;
    dispatch(reset());
    fetchData(
      finalLat,
      finalLng,
      radius,
      make,
      model,
      priceVal,
      yearVal,
      milesVal,
      condition,
      horsepowerVal,
      extColors,
      seller,
      cleanTitle
    );

    Cookies.set('location', JSON.stringify({ lat: finalLat, lng: finalLng }), {
      expires: cookieExpiryDays,
    });
  };

  const fetchData = (
    lat,
    lng,
    radius = initialRadus,
    make = '',
    model = '',
    priceVal = [priceMin, priceMax],
    yearVal = [yearMin, yearMax],
    milesVal = [milesMin, milesMax],
    condition = initCondition,
    horsepowerVal = [horsepowerMin, horsepowerMax],
    extColors = initExtColors,
    seller = initSeller,
    cleanTitle = iniCleanTitle
  ) => {
    dispatch(fetchWpCars({ pageKeyWp, category }))
      .unwrap()
      .then((response) => {
        wpTotalPages = response.headers['x-wp-totalpages'];
        // ITERATE WP API
        if (pageKeyWp < wpTotalPages) {
          pageKeyWp++;
          fetchData(
            lat,
            lng,
            radius,
            make,
            model,
            priceVal,
            yearVal,
            milesVal,
            condition,
            horsepowerVal,
            extColors,
            seller,
            cleanTitle
          );
        } else {
          const callMcApi = () => {
            let params = {
              pageKeyMc,
              lat,
              lng,
              radius,
              make,
              model,
              priceVal,
              yearVal,
              milesVal,
              condition,
              horsepowerVal,
              extColors,
              seller,
              cleanTitle,
              otherApiCategory,
            };
            dispatch(fetchMcCars(params));
          };

          // ITERATE MC API
          while (pageKeyMc < mcTotalPages) {
            callMcApi();
            pageKeyMc++;
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const searchFilter = () => {
    const filteredData = originalData.filter((item) => {
      const title = item.title?.rendered || item.heading;
      return title.toLowerCase().includes(searchTerm.toLowerCase());
    });

    dispatch(setDataBySearch(filteredData));
  };

  useEffect(() => {
    dispatch(reset());
    const locationCookie = Cookies.get('location');

    const handleLocation = (lat, lng) => {
      dispatch(changeLocation({ lat, lng }));
      fetchData(lat, lng);
      setInit(true);
    };

    if (locationCookie) {
      const parsedCookie = JSON.parse(locationCookie);
      handleLocation(parsedCookie.lat, parsedCookie.lng);
    } else {
      const getLocation = () => {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              handleLocation(latitude, longitude);
            },
            () => {
              console.log('Location access denied or error occurred.');
              handleLocation(lat, lng); // Fallback to default coordinates
            }
          );
        } else {
          console.log('Geolocation is not supported in this browser.');
          handleLocation(lat, lng); // Fallback to default coordinates
        }
      };
      getLocation();
    }
  }, [dispatch]);

  return (
    <div className='main-wrapper'>
      <Filters
        onFilter={filter}
        data={cars}
        stateCategory='motorcycles'
        filters={filterOptions}
      />
      <FiltersMobile
        onFilter={filter}
        data={cars}
        stateCategory='motorcycles'
        filters={filterOptions}
      />
      <div className='content'>
        {isLoading ? (
          <LoadingSpinner
            text='Please wait...'
            height='80'
            width='80'
            strokeWidth='4'
            animationDuration='1.0'
          />
        ) : (
          <div>
            <div className='top-action-wrap'>
              <div className='left-panel'>
                <span className='results-tag hide-mobile'>
                  {cars.length} Results
                </span>
                <div className='layout-buttons'>
                  <div>
                    <span>SORT BY:</span>
                    <SortBy onSortBy={handleSortBy} />
                  </div>
                  <div>
                    <span>PER PAGE:</span>
                    <PerPage onChangePage={perPageUpdate} />
                  </div>
                </div>
              </div>
              <div className='right-panel hide-mobile'>
                {/* <SearchFilter
                  onSearch={searchFilter}
                  stateCategory='motorcycles'
                /> */}
                <div className='layout-buttons'>
                  <span>VIEW:</span>
                  <button
                    onClick={() => setGridLayout(true)}
                    className={`${gridLayout ? 'active' : ''}`}
                  >
                    <CiGrid41 size={20} />
                  </button>
                  <button
                    onClick={() => setGridLayout(false)}
                    className={`${!gridLayout ? 'active' : ''}`}
                  >
                    <CiGrid2H size={20} />
                  </button>
                </div>
              </div>
            </div>
            <FeaturedCars data={cars} />
            <VehicleComponent data={carsData} type={otherApiCategory} />
            {(() => {
              switch (sortBy) {
                case 'Closest to Me':
                  if (carsData.length < cars.length) {
                    return (
                      <button onClick={loadMore} className='primary'>
                        {isLoading ? 'Loading...' : 'Load More'}
                      </button>
                    );
                  } else {
                    if (init) {
                      return (
                        <p
                          className={`${init ? 'end-result-tag' : 'd-none'}`}
                          dangerouslySetInnerHTML={{
                            __html:
                              carsData && cars.length
                                ? endOfListMessage
                                : noResultsMessage,
                          }}
                        ></p>
                      );
                    }
                  }
                default:
                  if (carsData.length < originalSorted_data.length) {
                    switch (true) {
                      case sorted_data.length === 0:
                        return (
                          <p
                            className={`${init ? 'end-result-tag' : 'd-none'}`}
                            dangerouslySetInnerHTML={{
                              __html: noResultsMessage,
                            }}
                          ></p>
                        );
                      case carsData.length < sorted_data.length:
                        return (
                          <button onClick={loadMore} className='primary'>
                            {isLoading ? 'Loading...' : 'Load More'}
                          </button>
                        );
                      default:
                        return (
                          <p
                            className={`${init ? 'end-result-tag' : 'd-none'}`}
                            dangerouslySetInnerHTML={{
                              __html: endOfListMessage,
                            }}
                          ></p>
                        );
                    }
                  } else {
                    return (
                      <p
                        className={`${init ? 'end-result-tag' : 'd-none'}`}
                        dangerouslySetInnerHTML={{
                          __html:
                            carsData && sorted_data.length
                              ? endOfListMessage
                              : noResultsMessage,
                        }}
                      ></p>
                    );
                  }
              }
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Motorcycles;
